import Intercom, {
  onUnreadCountChange,
  showMessages,
  showSpace,
  update,
} from '@intercom/messenger-js-sdk';

import { afterPageLoad } from './after-page-load';

type IntercomBootSettings = Parameters<typeof Intercom>[0];
type IntercomUpdateSettings = Parameters<typeof update>[0];

/**
 * Deferred Intercom.
 *
 * The SDK downloads the messenger (~280 KB) the moment it is booted, so the
 * boot waits until the page has loaded. Everything the app says to Intercom in
 * the meantime is held here and replayed on boot: the SDK itself drops calls
 * made before boot. Opening the messenger boots immediately — a click is the
 * user asking for it.
 */
let bootSettings: IntercomBootSettings | undefined;
let booted = false;
// Keyed so that only the latest call of each kind is replayed.
const pending = new Map<string, () => void>();

function boot(): void {
  if (booted || !bootSettings) return;
  booted = true;
  Intercom(bootSettings);
  pending.forEach((call) => call());
  pending.clear();
}

function whenBooted(key: string, call: () => void): void {
  if (booted) {
    call();
  } else {
    pending.set(key, call);
  }
}

/**
 * Boots Intercom once the page has finished loading.
 * @param settings - The settings to boot the messenger with.
 * @returns A function that cancels the pending boot.
 */
export function bootIntercomAfterPageLoad(
  settings: IntercomBootSettings,
): () => void {
  bootSettings = settings;
  return afterPageLoad(boot);
}

export function updateIntercom(settings: IntercomUpdateSettings): void {
  whenBooted('update', () => update(settings));
}

export function onIntercomUnreadCountChange(
  callback: (unreadCount: number) => void,
): void {
  whenBooted('unread', () => onUnreadCountChange(callback));
}

export function showIntercomMessages(): void {
  boot();
  showMessages();
}

export function showIntercomSpace(spaceName: string): void {
  boot();
  showSpace(spaceName);
}
